import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { buildSearch, getWizardV2State } from '../wizardService';
import { selectSecondaryFilter, updateAllSecondaryFilter } from '../../secondaryFilter/secondaryFilterSlice';
import { updateDatesFilter, updateMainFilter, updateWhoFilter } from '../../mainFilter/mainFilterSlice';
import { selectFilterData } from '../../../features/mainFilter/selectors';
import { updateSearch } from '../../mainFilter/searchSlice';
import { setWizardDesktop, setMobileHomepage, hideOnboarding } from '../onboardingSlice';
import { setLastChangedFilter } from '../../../appSlice';
import useStartWizardSearch from '../useStartWizardSearch';
import { scrollToResults } from '../../../utilities/helpers';
import showTrustBuilder from '../../../utilities/showTrustBuilder';
import useUserPreferences from '../../../hooks/preferences/useUserPreferences';
import { useAppSelector } from '../../../hooks/hooks';
import { removeEntryFlowParams } from '../../user/userSlice';

const LoadingStep = () => {
  const dispatch = useDispatch();
  const { formV2: form, initiator, stepNames, widgetStartSearch, show, showDesktop } = useSelector(state => state.onboarding);
  const { user, entryFlowParams } = useAppSelector(state => state.user);
  const { preferences } = useUserPreferences(user?.muid || '');
  const loadingStepData = getWizardV2State().loadingStep;
  const { searchEngine } = useSelector(selectFilterData);
  const secondaryFilter = useSelector(selectSecondaryFilter);
  const { startSearch } = useStartWizardSearch();

  useEffect(() => {
    let timeout;
    if (widgetStartSearch) {
      timeout = setTimeout(() => {
        console.log('postMessage:', widgetStartSearch);
        window.parent.postMessage(widgetStartSearch, '*');
      }, 4000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [widgetStartSearch]);

  useEffect(() => {
    const searchState = buildSearch(form, entryFlowParams);
    if (initiator === 'customize') {
      function getMappedData(stateValue, engineData) {
        if (Array.isArray(stateValue)) {
          return engineData.map(i => ({ ...i, active: stateValue.includes(i.id) }));
        } else if (typeof stateValue === 'number') {
          return engineData.map(i => ({ ...i, active: i.id === stateValue }));
        } else {
          return engineData.map(i => ({ ...i, active: false }));
        }
      }

      const whereData = {
        destinations: getMappedData(searchState.destinations, searchEngine.where.destinations),
        destinationsGroups: getMappedData(searchState.destinationsGroups, searchEngine.where.destinationsGroups)
      };

      const whenData = {
        type: searchState.type,
        months: searchState.months,
        nights: searchState.nights,
        startDate: searchState.startDate ? searchState.startDate.replace(/-/g, '/') : null,
        endDate: searchState.endDate ? searchState.endDate.replace(/-/g, '/') : null,
      }

      const whoData = {
        adults: searchState.adults,
        children: searchState.children || 0,
        childAges: searchState.childAges || [],
        rooms: searchState.rooms || 1
      }

      const boardFilters = secondaryFilter.data.board.map((item) => ({ ...item, active: false }))
      if (preferences?.breakfast_quality) {
        boardFilters[0].active = true;
      }

      dispatch(updateAllSecondaryFilter({
        updatedFilters: {
          board: boardFilters,
          stars: secondaryFilter.data.stars.map((item) => ({ ...item, active: form.style?.stars?.includes(item.value) })),
          sort: secondaryFilter.data.sort.map((item, ind) => (ind === 0 ? { ...item, active: true } : { ...item, active: false })),
          rating: secondaryFilter.data.rating.map((item) => ({ ...item, active: false })),
          facilities: secondaryFilter.data.facilities.map((item) => ({ ...item, active: false })),
          stops: secondaryFilter.data.stops.map((item) => ({ ...item, active: false })),
          duration: {
            ...secondaryFilter.data.duration,
            activeTime: secondaryFilter.data.duration.maxTime,
          },
          luggage: secondaryFilter.data.luggage.map((item) => ({ ...item, active: false })),
          freeCancellation: false,
        }
      }));

      const selectedWhat = form.experience?.id;
      const whatState = selectedWhat
      ? searchEngine.what.map(i => ({ ...i,  active: selectedWhat === i.id }))
      : searchEngine.what.map(i => ({ ...i, active: false }));

      dispatch(updateMainFilter({
        filter: 'what',
        updatedValue: whatState
      }));
      dispatch(updateMainFilter({
        updatedValue: whereData,
        filter: 'where',
      }));
      dispatch(updateDatesFilter(whenData));
      dispatch(updateWhoFilter(whoData));
      dispatch(updateSearch({ who: whoData }));
      dispatch(setLastChangedFilter('wizard'));
    } else {
      if (!stepNames.includes('email') && (show || showDesktop)) {
        startSearch();
      }
    }

    const hideStaticWizard = () => {
      document.getElementById('mobile-preload-container')?.remove();
    };

    const timeout = setTimeout(() => {
      setTimeout(() => {
        sessionStorage.removeItem('hideWhatTheme')
        dispatch(removeEntryFlowParams());
      }, 200);
      dispatch(setWizardDesktop(false));
      dispatch(setMobileHomepage(false));

      hideStaticWizard();
      dispatch(hideOnboarding());
      scrollToResults();
      if (typeof window.initiateOneTapLogin === 'function') {
        window.initiateOneTapLogin();
      }
      showTrustBuilder(dispatch);
      document.dispatchEvent(new CustomEvent('wizard closed'));
    }, (loadingStepData.animationTimer || 5500));


    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch]);

  return (
    <div className="wizard-step wizard-loading-step justify-center">
      {loadingStepData.loadingText && <p className="font-semibold text-secondary text-lg text-center">{loadingStepData.loadingText}</p>}
      <div className="wizard-splash__loader mt-10 self-center" />
    </div>
  )
}

export default LoadingStep;
